import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  updateProfile,
  sendPasswordResetEmail,
} from 'firebase/auth';

import api from './client';
import { firebaseAuth, googleProvider } from '../config/firebase';
import { User } from '../types';

export interface RegisterInput {
  email: string;
  password: string;
  displayName: string;
}

export interface LoginInput {
  email: string;
  password: string;
}

/**
 * All flows end with a call to POST /api/auth/sync, which creates the user
 * row in our DB on first login and returns the full profile (including
 * is_verified, is_admin). The backend trusts the Firebase ID token.
 */
export const authApi = {
  async register(input: RegisterInput): Promise<User> {
    const cred = await createUserWithEmailAndPassword(
      firebaseAuth,
      input.email,
      input.password
    );
    // Set the displayName on the Firebase user so it flows through in token
    // claims from next request onward.
    await updateProfile(cred.user, { displayName: input.displayName });
    // Force a token refresh so the next /sync call includes the name claim.
    await cred.user.getIdToken(true);
    return syncProfile(input.displayName);
  },

  async login(input: LoginInput): Promise<User> {
    await signInWithEmailAndPassword(firebaseAuth, input.email, input.password);
    return syncProfile();
  },

  async loginWithGoogle(): Promise<User> {
    await signInWithPopup(firebaseAuth, googleProvider);
    return syncProfile();
  },

  async logout(): Promise<void> {
    await signOut(firebaseAuth);
  },

  async resetPassword(email: string): Promise<void> {
    await sendPasswordResetEmail(firebaseAuth, email);
  },

  async getMe(): Promise<User> {
    const { data } = await api.get<User>('/auth/me');
    return data;
  },

  /**
   * Call /auth/sync directly. Used by the auth state observer to create the
   * local user row on first login (including Google popup flows).
   */
  async sync(displayName?: string): Promise<User> {
    return syncProfile(displayName);
  },
};

async function syncProfile(displayName?: string): Promise<User> {
  const { data } = await api.post<{ user: User }>('/auth/sync', {
    displayName,
  });
  return data.user;
}
